import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { toaster } from '../../App'
import { ArrowBackButton } from '../../atoms/Buttons/ArrowBackButton'
import { Button } from '../../atoms/Buttons/Button'
import LoadingSpinner from '../../atoms/Loading/LoadingSpinner'
import { ReactComponent as NewWindowIcon } from '../../common/assets/images/new-window-icon.svg'
import { CaptureMethodSelection } from '../../molecules/CaptureMethodSelection/CaptureMethodSelection'
import OperatorProfileInformation from '../../molecules/Operators/operator/OperatorProfileInformation'
import { StoreSelection } from '../../molecules/StoreSelection/StoreSelection'
import ConfigurationsSliceReducer from '../../redux/reducers/configurations/configurations.reducer'
import { useTypedSelector } from '../../redux/reducers/selectors'
import { bffParceirosApiService } from '../../services/bff'
import { StoreDto } from '../../services/bff/retailers/dto/Store.dto'
import { UserSessionService } from '../../services/userSession/user-session.service'
import { PhoneLayout } from '../../ui/PhoneLayout'
import { gray, green, white } from '../../ui/utils/_colors'
import PhoneUtils from '../../utils/phone.utils'
import { sleep } from '../../utils/sleep'
import { OperatorCenterHelper } from './OperatorCenterHelper'

const OperatorConfiguration: React.FC = () => {
  const { isFetchingOperator, operator, configurations } = useTypedSelector(state => ({
    isFetchingOperator: state.operator.isFetchingOperator,
    operator: state.operator.operator,
    configurations: state.configurations.configurations,
  }))

  const [shouldRenderSuccessMessage, setRenderSuccessMessage] = useState<boolean>(false)
  const [isRedirectingPage, setIsRedirectingPage] = useState<boolean>(false)
  const localStorageStore = bffParceirosApiService.coordinator.getStore()

  const dispatch = useDispatch()

  useEffect(() => {
    UserSessionService.fetchOperator(dispatch)
  }, [])

  const handleBackButton = () => {
    if (operator) return OperatorCenterHelper.redirectToOperatorHome(configurations, operator.role)
  }

  const handleUpdateOperatorDataOnClick = async () => {
    setIsRedirectingPage(true)

    try {
      const updateOperatorDataLink = await bffParceirosApiService.retailersOnboarding.getOperatorsDataValidationLink({ document: operator!.cpf })

      setIsRedirectingPage(false)
      window.open(updateOperatorDataLink.url, '_blank')
    } catch {
      setIsRedirectingPage(false)
      toaster.showErrorToast('Ocorreu um erro, tente novamente.')
    }
  }

  const handleSelectedStore = async (selectedStore?: StoreDto) => {
    if (selectedStore) {
      dispatch(ConfigurationsSliceReducer.actions.fetchConfigurations({}))

      setRenderSuccessMessage(true)
      await sleep(3000)
      setRenderSuccessMessage(false)
    }
  }

  const shouldRenderCaptureMethodConfig = !!localStorageStore?.settings?.captureMethod

  if (isFetchingOperator) return <LoadingSpinner />
  if (isRedirectingPage) return <LoadingSpinner text={'Levando você para um ambiente seguro...'} />

  return (
    <>
      <MainContainer>
        <BackButtonContainer>
          <ArrowBackButton onBackButtonClick={handleBackButton} />
        </BackButtonContainer>

        <ConfigurationContainer>
          <Title>Configurações</Title>

          <StoreConfiguration>
            <Subtitle>Alterar Loja</Subtitle>
            <StoreSelection handleSelectedStore={handleSelectedStore} updatingStoreLoadingText={'Alterando a loja...'} />
            {shouldRenderSuccessMessage && <SuccessMessage>Loja alterada com sucesso!</SuccessMessage>}
          </StoreConfiguration>

          <Line />

          {localStorageStore && shouldRenderCaptureMethodConfig && (
            <>
              <CaptureMethod>
                <Subtitle style={{ margin: '8px 0px' }}>Alterar Padrão de Vendas</Subtitle>
                <Text>Escolha se deseja fazer as vendas com a Ume identificando o cliente via CPF ou utilizando QR Code.</Text>
                <CaptureMethodSelection store={localStorageStore} />
              </CaptureMethod>
              <Line />
            </>
          )}

          <OperatorProfile>
            <Subtitle>Dados cadastrais</Subtitle>
            <OperatorProfileInformation title={'Nome'} value={operator?.name} warningMessage={'Sem nome cadastrado'} />
            <OperatorProfileInformation title={'Telefone'} value={PhoneUtils.maskPhone(operator?.phoneNumber)} warningMessage={'Sem telefone cadastrado'} />
            <OperatorProfileInformation title={'E-mail'} value={operator?.email} warningMessage={'Sem e-mail cadastrado'} />
            <OperatorProfileInformation title={'Chave Pix'} value={operator?.pixAccount?.pixKey} warningMessage={'Sem chave Pix cadastrada'} />
          </OperatorProfile>
        </ConfigurationContainer>
        <Footer>
          {operator && (
            <Button
              text={'Alterar dados cadastrais'}
              textColor={green.dark2}
              backgroundColor={white.primary}
              border={`1px solid ${green.dark2}`}
              icon={NewWindowIcon}
              onClick={handleUpdateOperatorDataOnClick}
            />
          )}
        </Footer>
      </MainContainer>
    </>
  )
}

export default OperatorConfiguration

const MainContainer = styled(PhoneLayout).attrs({
  style: {
    padding: '24px 16px 0px 16px',
  },
})``

const BackButtonContainer = styled.div``

const Title = styled.text`
  font-family: Inter;
  font-size: 18px;
  font-weight: 700;
  text-align: left;
  line-height: 22px;
`

const ConfigurationContainer = styled.div`
  margin-top: 16px;
  text-align: left;
`

const StoreConfiguration = styled.div`
  margin: 24px 0px;
`

const SuccessMessage = styled.div`
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  color: #448017;
`

const OperatorProfile = styled.div`
  margin-top: 24px;
`

const Subtitle = styled.h2`
  font-family: Inter;
  font-size: 16px;
  font-weight: 700;
  text-align: left;
  margin: 16px 0px;
`

const Text = styled.text`
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  text-align: left;
  line-height: 16px;
  color: ${gray.scale700};
`

const CaptureMethod = styled.div`
  margin: 24px 0px;
`

const Line = styled.hr`
  color: #7f7f7f;
  opacity: 0.3;
  border-bottom-width: 1px;
  width: 100%;
  margin: 16px 0px 24px 0px;
`

const Footer = styled.div`
  margin-top: 8px;
  width: 100%;
  flex-direction: column;
  align-items: center;
  bottom: 16px;
`
